import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { RevealHeading } from "@/components/RevealHeading";
import { MotionInView } from "@/components/MotionInView";
import { industries } from "@/content/industries";
import { company } from "@/content/site";

export default function Industries() {
  return (
    <>
      <Seo
        title={`Industries We Serve | ${company.name}`}
        description="Tanker freight for chemicals, lubricants, oil and gas, rubber and tire, paints and coatings, personal care, bioenergy, water treatment, agriculture, and manufacturing - hauled with the right equipment and endorsements."
      />

      {/* HEADER */}
      <section className="relative overflow-hidden bg-brand-900 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 10px)",
          }}
        />
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-accent-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-[1200px] px-6 py-20 lg:py-24">
          <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-400">Industries</div>
          <RevealHeading
            as="h1"
            text="Specialized lanes, specialized teams."
            className="mb-5 max-w-3xl text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.05]"
          />
          <p className="max-w-2xl text-lg text-brand-200">
            Every vertical has its own rules, equipment, and paperwork. {company.shortName}{" "}
            runs the right tankers and endorsements for each one - so your freight
            arrives in spec, on time, and fully documented.
          </p>
        </div>
      </section>

      {/* QUICK-JUMP TILES */}
      <section className="border-b border-brand-100 bg-brand-50 py-10">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <MotionInView key={ind.slug} delay={(i % 5) * 0.07} y={20}>
                  <a
                    href={`#${ind.slug}`}
                    className="group flex flex-col items-center gap-2 rounded-lg border border-brand-100 bg-white p-4 text-center transition-all hover:-translate-y-1 hover:border-accent-500 hover:shadow-md"
                  >
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-brand-900 text-white transition-colors group-hover:bg-accent-500">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="text-sm font-semibold text-brand-900">{ind.name}</span>
                  </a>
                </MotionInView>
              );
            })}
          </div>
        </div>
      </section>

      {/* DETAIL SECTIONS */}
      {industries.map((ind, i) => {
        const Icon = ind.icon;
        const flip = i % 2 === 1;
        return (
          <section
            key={ind.slug}
            id={ind.slug}
            className={cn("scroll-mt-24 py-16 lg:py-20", i % 2 === 1 && "bg-brand-50")}
          >
            <div className="mx-auto grid max-w-[1200px] items-center gap-10 px-6 lg:grid-cols-2">
              <Reveal className={cn(flip && "lg:order-2")}>
                <div className="relative aspect-[4/3] overflow-hidden rounded-lg border border-brand-100 bg-brand-900">
                  {ind.image ? (
                    <img src={ind.image} alt={ind.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-tr from-brand-900 via-brand-800 to-brand-700" />
                  )}
                </div>
              </Reveal>

              <Reveal delay={120} className={cn(flip && "lg:order-1")}>
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500/10 text-accent-500">
                  <Icon className="h-6 w-6" />
                </div>
                <h2 className="mb-4 text-3xl lg:text-4xl">{ind.name}</h2>
                <p className="mb-6 max-w-prose text-lg text-brand-600">{ind.detail}</p>
                <div className="mb-7">
                  <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-brand-400">
                    Typical loads
                  </div>
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {ind.examples.map((ex) => (
                      <li key={ex} className="flex items-center gap-2 text-sm text-brand-700">
                        <Check className="h-4 w-4 shrink-0 text-accent-500" /> {ex}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link to="/contact" className={cn(buttonVariants({ variant: "accent" }), "w-full justify-center sm:w-auto")}>
                  Get a quote <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Reveal>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="bg-brand-900 py-16 text-white">
        <Reveal className="mx-auto flex max-w-[1200px] flex-col items-center justify-between gap-6 px-6 md:flex-row">
          <div>
            <RevealHeading text="Don't see your product?" className="text-3xl lg:text-4xl" />
            <p className="mt-2 max-w-xl text-brand-300">
              If it moves in a tanker, chances are we haul it. Tell us what you need
              to move and we'll find the right equipment and lane.
            </p>
          </div>
          <div className="flex w-full shrink-0 flex-wrap gap-3 md:w-auto">
            <Link to="/contact" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full justify-center sm:w-auto")}>
              Talk to a dispatcher
            </Link>
            <Link
              to="/services"
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "w-full justify-center border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white sm:w-auto",
              )}
            >
              View services
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
